'use client'

import { useTranslation } from '@/hooks/client/use-translation'
import SvgPhone from '@/icons/thin/Phone'
import { popupEventName } from '@/lib/popups/constants'
import type { SitePopup } from '@/lib/popups/types'
import { cx } from '@/lib/utils'

import { popupClickTracking } from './click-tracking'

interface GeneralPopupPhoneLinkProps {
  popup: SitePopup
  onCtaClick: () => void
  variant: 'inline' | 'button'
  className?: string
  reason?: string | null
}

const phoneHref = (number: string): string => {
  const digits = number.replace(/\D/g, '')
  return `tel:${number.trim().startsWith('+') ? '+' : ''}${digits}`
}

export const GeneralPopupPhoneLink = ({
  popup,
  onCtaClick,
  variant,
  className,
  reason,
}: GeneralPopupPhoneLinkProps) => {
  const { t } = useTranslation()
  if (!popup.phoneNumber) {
    return null
  }

  return (
    <a
      href={phoneHref(popup.phoneNumber)}
      {...popupClickTracking(reason)}
      data-event-name={popupEventName(popup.slug)}
      data-event-position={`${popup.slug}-call`}
      data-event-origin={popup.slug}
      data-event-product={popup.title ?? popup.slug}
      onClick={onCtaClick}
      className={cx(
        variant === 'inline'
          ? 'text-lapis cursor-pointer items-center justify-center gap-2 text-[15px] font-semibold hover:underline'
          : 'border-navy text-navy hover:bg-navy/5 w-full cursor-pointer items-center justify-center gap-2 rounded-lg border py-3.5 text-sm font-bold transition-colors',
        className
      )}
    >
      <SvgPhone className={variant === 'inline' ? 'h-[15px] w-[15px]' : 'h-4 w-4'} aria-hidden="true" />
      {variant === 'inline'
        ? popup.phoneNumber
        : t('generalPopup.call', 'Call {{phone}}', { phone: popup.phoneNumber })}
    </a>
  )
}
